import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { LanguageSelect } from '@/components/LanguageSelect';
import { authT } from '@/i18n/messages/auth';

export function LoginPage() {
  const t = authT().login;
  const { signInWithGoogle, signInWithEmail, loading: authLoading, configured, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = (location.state as { from?: string } | null)?.from ?? '/events';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signInWithEmail(email, password);
      navigate(redirect, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle() {
    setSubmitting(true);
    setError(null);
    try {
      await signInWithGoogle();
      navigate(redirect, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // Já autenticado (ex.: voltou pra /login pelo histórico ou abriu o link
  // direto): não mostra o form de novo, manda pro destino. Depois de todos
  // os hooks pra não violar as regras do React.
  if (user) {
    return <Navigate to={redirect} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          {/* Assinatura (símbolo + wordmark) em tinta preta — `dark:invert`
              porque o tema escuro do painel deixaria o logo invisível. */}
          <img
            src="/windmaps-logo.svg"
            alt="WindMaps"
            className="w-56 h-auto mb-2 dark:invert"
          />
          <CardDescription>{t.subtitle}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!configured && (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
              {t.firebaseNotConfigured(
                // i18n-ignore nome de variável de ambiente: identificador, igual em todo idioma
                <code className="rounded bg-muted px-1">VITE_FIREBASE_*</code>,
                // i18n-ignore nome de arquivo: identificador, igual em todo idioma
                <code className="rounded bg-muted px-1">.env.local</code>,
                // i18n-ignore nome de arquivo do repo: identificador, igual em todo idioma
                <code>PERMISSIONS_CHECKLIST.md</code>,
              )}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 gap-2"
            disabled={submitting || authLoading || !configured}
            onClick={handleGoogle}
          >
            {submitting ? (
              <Spinner className="mr-2" />
            ) : (
              // Logo oficial do Google ("G" multicolor). Não tinta com
              // currentColor — precisa preservar as cores da marca.
              <img src="/google_g.svg" alt="" className="h-5 w-5" width={20} height={20} />
            )}
            {t.continueWithGoogle}
          </Button>
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">{t.or}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t.emailLabel}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t.passwordLabel}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full h-11" disabled={submitting || authLoading || !configured}>
              {submitting ? <Spinner className="mr-2" /> : null}
              {t.signIn}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex-wrap gap-3 text-xs text-muted-foreground">
          <Link to="/privacy" className="underline">
            {t.privacyPolicy}
          </Link>
          <Link to="/terms" className="underline">
            {t.terms}
          </Link>
          <LanguageSelect className="ml-auto" />
        </CardFooter>
      </Card>
    </div>
  );
}
